import { cn } from 'src/utils'
import useAuth from '../hooks/useAuth'
import logo from '../logo.svg'
import { Link } from 'react-router-dom'
import { FEEDBACK_FORM_URL, IS_WEB } from 'src/constants'
import { LibraryIcon } from 'lucide-react'

const DemoBanner = ({
  onSignUp,
}: {
  onSignUp: () => void
}) => (
  <section className="align-center relative flex flex-col justify-center gap-4 rounded-sm bg-yellow-200 px-4 py-8">
    <div className="mx-auto max-w-lg pt-16 font-mono text-sm text-yellow-600">
      Viewing the demo version of Lahilo.{' '}
      <span
        className="border-b-[0.125rem] border-b-yellow-600 hover:cursor-pointer hover:opacity-80"
        onClick={onSignUp}
      >
        Sign in
      </span>{' '}
      or{' '}
      <span
        className="border-b-[0.125rem] border-b-yellow-600 hover:cursor-pointer hover:opacity-80"
        onClick={onSignUp}
      >
        create an account
      </span>{' '}
      to access hundreds of books and save your progress.
    </div>
  </section>
)

const SimpleLayout: React.FC<
  React.PropsWithChildren<JSX.IntrinsicElements['div']> & {
    footer?: React.ReactNode
    simple?: boolean
  }
> = ({ children, footer, className, simple = false }) => {
  const { logout, login, isLoading, isLoggedIn } = useAuth()

  if (isLoading) return <div></div>

  return (
    <>
      {!isLoading && !isLoggedIn && (
        <div className="mb-4 w-[100vw]">
          <DemoBanner onSignUp={login} />
        </div>
      )}
      <div
        className={cn(
          'absolute relative mt-12 flex h-full max-h-[100vh] min-h-screen w-full flex-col items-center justify-center overflow-x-hidden bg-white p-0 md:bg-white',
          className,
        )}
      >
        <header className="mb-4 h-12 w-full select-none">
          <img
            src={logo}
            alt="Lahilo"
            className="mx-auto mt-4 h-8"
          />
          {!isLoggedIn && (
            <button
              className="absolute top-4 right-4 text-slate-800 transition-all ease-out hover:text-slate-600"
              onClick={login}
            >
              Sign In
            </button>
          )}
          {isLoggedIn && (
            <button
              className="absolute top-4 right-4 text-slate-800 transition-all ease-out hover:text-slate-600"
              onClick={logout}
            >
              Logout
            </button>
          )}
          <Link to="/">
            <div className="absolute top-4 left-4 text-slate-800 transition-all ease-out hover:text-slate-600">
              <LibraryIcon />
            </div>
          </Link>
        </header>

        {children}

        {!simple && (
          <section className="mt-auto flex min-h-[10rem] w-full max-w-sm flex-col items-center justify-end">
            <div className="align-center flex w-full justify-around gap-2">
              {footer}
              <Link
                className="flex items-center border-b border-b-transparent text-sm transition-all ease-in-out hover:border-b hover:border-b-slate-500"
                to={`/tutorials/reader?returnTo=${encodeURIComponent(
                  window.location.pathname,
                )}`}
              >
                Tutorial
              </Link>
              {IS_WEB && (
                <Link
                  className="flex items-center border-b border-b-transparent text-sm transition-all ease-in-out hover:border-b hover:border-b-slate-500"
                  to={`/tutorials/install-app?returnTo=${encodeURIComponent(
                    window.location.pathname,
                  )}`}
                >
                  Install as an App
                </Link>
              )}
            </div>

            <div className="pt-4">
              <Link
                to="/account/privacy-policy"
                className="border-b border-b-transparent text-left text-xs text-slate-400 transition-all ease-in-out hover:cursor-pointer hover:border-b-slate-400"
              >
                Privacy Policy
              </Link>
            </div>

            <Link
              to={FEEDBACK_FORM_URL!}
              target="_blank"
              className="w-full"
            >
              <p className="my-4 flex w-full justify-center gap-2 rounded-t-sm border-2 border-slate-50 border-b-transparent bg-slate-50 px-4 pt-4 pb-3 text-center text-sm text-slate-500 transition-all ease-in-out hover:gap-4 hover:border-2 hover:border-slate-500 hover:border-b-transparent hover:text-slate-600">
                <span>Submit Feedback</span>→
              </p>
            </Link>
          </section>
        )}
      </div>
    </>
  )
}

export default SimpleLayout
